import { useState } from "react";

const FORMATS = [
  { id: "linkedin", label: "LinkedIn Bio", icon: "in", free: true, desc: "Professional summary for your profile" },
  { id: "instagram", label: "Instagram Bio", icon: "ig", free: true, desc: "150-char punchy bio" },
  { id: "elevator", label: "Elevator Pitch", icon: "◎", free: true, desc: "30-second verbal intro" },
  { id: "twitter", label: "Twitter / X Bio", icon: "𝕏", free: false, desc: "160-char identity statement" },
  { id: "cold_email", label: "Cold Email Opener", icon: "✉", free: false, desc: "First line that gets replies" },
];

const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Mono:wght@300;400&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0c0c0c;
    --surface: #141414;
    --border: #252525;
    --accent: #e8d5a3;
    --accent2: #c4a882;
    --text: #f0ece4;
    --muted: #6b6560;
    --paid: #2a2118;
    --paid-border: #4a3a28;
  }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: 'DM Mono', monospace;
    min-height: 100vh;
  }

  .app {
    max-width: 780px;
    margin: 0 auto;
    padding: 60px 24px 100px;
  }

  /* HEADER */
  .header {
    margin-bottom: 64px;
  }

  .logo {
    font-family: 'Playfair Display', serif;
    font-size: 13px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 40px;
    opacity: 0.7;
  }

  .headline {
    font-family: 'Playfair Display', serif;
    font-size: clamp(36px, 6vw, 62px);
    line-height: 1.08;
    letter-spacing: -0.02em;
    margin-bottom: 16px;
  }

  .headline em {
    font-style: italic;
    color: var(--accent);
  }

  .subline {
    font-size: 12px;
    color: var(--muted);
    letter-spacing: 0.05em;
    line-height: 1.7;
    max-width: 420px;
  }

  /* FORM */
  .form-block {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 2px;
    padding: 32px;
    margin-bottom: 32px;
  }

  .form-label {
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 10px;
    display: block;
  }

  .form-hint {
    font-size: 11px;
    color: var(--muted);
    margin-bottom: 14px;
    font-style: italic;
  }

  .form-row {
    margin-bottom: 24px;
  }

  textarea, input[type="text"] {
    width: 100%;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 2px;
    color: var(--text);
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    padding: 14px 16px;
    resize: none;
    outline: none;
    transition: border-color 0.2s;
    line-height: 1.6;
  }

  textarea:focus, input[type="text"]:focus {
    border-color: var(--accent2);
  }

  textarea::placeholder, input::placeholder {
    color: var(--muted);
  }

  /* GENERATE BTN */
  .btn-generate {
    width: 100%;
    background: var(--accent);
    color: #0c0c0c;
    border: none;
    border-radius: 2px;
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    font-weight: 400;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    padding: 18px;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.1s;
    position: relative;
    overflow: hidden;
  }

  .btn-generate:hover { opacity: 0.88; }
  .btn-generate:active { transform: scale(0.995); }
  .btn-generate:disabled { opacity: 0.35; cursor: not-allowed; }

  /* RESULTS */
  .results-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    margin-top: 48px;
  }

  .results-title {
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    letter-spacing: -0.01em;
  }

  .results-line {
    flex: 1;
    height: 1px;
    background: var(--border);
  }

  .format-card {
    border: 1px solid var(--border);
    border-radius: 2px;
    margin-bottom: 16px;
    overflow: hidden;
    animation: fadeUp 0.4s ease forwards;
    opacity: 0;
  }

  .format-card:nth-child(1) { animation-delay: 0.05s; }
  .format-card:nth-child(2) { animation-delay: 0.12s; }
  .format-card:nth-child(3) { animation-delay: 0.19s; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .format-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
  }

  .format-card-label {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--muted);
  }

  .format-icon {
    font-size: 11px;
    color: var(--accent);
  }

  .copy-btn {
    background: none;
    border: 1px solid var(--border);
    border-radius: 2px;
    color: var(--muted);
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    padding: 6px 12px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .copy-btn:hover {
    border-color: var(--accent2);
    color: var(--accent);
  }

  .copy-btn.copied {
    border-color: #4a7c59;
    color: #6aaf80;
  }

  .format-content {
    padding: 24px 20px;
    font-size: 14px;
    line-height: 1.75;
    color: var(--text);
    white-space: pre-wrap;
  }

  /* PAYWALL CARD */
  .paywall-section {
    margin-top: 16px;
  }

  .paywall-card {
    border: 1px solid var(--paid-border);
    border-radius: 2px;
    background: var(--paid);
    overflow: hidden;
    margin-bottom: 16px;
    position: relative;
  }

  .paywall-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--paid-border);
  }

  .paywall-card-label {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent2);
    opacity: 0.6;
  }

  .locked-badge {
    font-size: 9px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--accent);
    border: 1px solid var(--paid-border);
    border-radius: 2px;
    padding: 4px 8px;
    opacity: 0.7;
  }

  .paywall-blur {
    padding: 24px 20px;
    font-size: 14px;
    line-height: 1.75;
    color: var(--muted);
    filter: blur(5px);
    user-select: none;
    pointer-events: none;
    opacity: 0.4;
  }

  .paywall-cta {
    background: var(--surface);
    border: 1px solid var(--accent);
    border-radius: 2px;
    margin: 0 20px 20px;
    padding: 20px;
    text-align: center;
  }

  .paywall-cta-title {
    font-family: 'Playfair Display', serif;
    font-size: 16px;
    margin-bottom: 6px;
    color: var(--accent);
  }

  .paywall-cta-sub {
    font-size: 11px;
    color: var(--muted);
    margin-bottom: 16px;
    line-height: 1.6;
  }

  .btn-unlock {
    background: var(--accent);
    color: #0c0c0c;
    border: none;
    border-radius: 2px;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    padding: 12px 28px;
    cursor: pointer;
    transition: opacity 0.2s;
  }

  .btn-unlock:hover { opacity: 0.85; }

  .price-tag {
    font-size: 11px;
    color: var(--muted);
    margin-top: 10px;
  }

  /* LOADER */
  .loader {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 32px 0;
    color: var(--muted);
    font-size: 11px;
    letter-spacing: 0.15em;
  }

  .dots span {
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--accent);
    margin: 0 2px;
    animation: dot-pulse 1.2s infinite;
  }
  .dots span:nth-child(2) { animation-delay: 0.2s; }
  .dots span:nth-child(3) { animation-delay: 0.4s; }

  @keyframes dot-pulse {
    0%, 60%, 100% { opacity: 0.2; transform: scale(0.9); }
    30% { opacity: 1; transform: scale(1.2); }
  }

  /* ERROR */
  .error-msg {
    background: #1a0d0d;
    border: 1px solid #3d1515;
    border-radius: 2px;
    padding: 16px 20px;
    font-size: 12px;
    color: #e07070;
    margin-top: 16px;
  }

  /* FOOTER */
  .footer {
    margin-top: 80px;
    padding-top: 24px;
    border-top: 1px solid var(--border);
    font-size: 11px;
    color: var(--muted);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .footer-brand {
    font-family: 'Playfair Display', serif;
    font-size: 13px;
    color: var(--accent);
    opacity: 0.5;
  }
`;

const PLACEHOLDER_BLUR = {
  twitter: "Helping founders find their voice. Building in public. DMs open for the brave.",
  cold_email: "I noticed you're growing your consulting practice — most experts I talk to struggle to explain what makes them different in one sentence. That's exactly what I solve.",
};

export default function SayWhatYouDo() {
  const [what, setWhat] = useState("");
  const [who, setWho] = useState("");
  const [outcome, setOutcome] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState({});
  const [unlocked] = useState(false);

  const canGenerate = what.trim().length > 5 && who.trim().length > 3 && outcome.trim().length > 3;

  async function generate() {
    setLoading(true);
    setError("");
    setResults(null);

    const prompt = `You are a world-class copywriter who specializes in personal branding and positioning.

Based on this person's info:
- What they do: ${what}
- Who they help: ${who}
- The outcome/result they deliver: ${outcome}

Generate exactly 5 formats. Respond ONLY with valid JSON, no markdown, no explanation:

{
  "linkedin": "...",
  "instagram": "...",
  "elevator": "...",
  "twitter": "...",
  "cold_email": "..."
}

Rules:
- linkedin: 3-4 sentences, professional, specific, first person. Start with the outcome, not job title.
- instagram: Max 150 chars. Punchy. Use line breaks with \\n. Can use 1-2 emojis max.
- elevator: 2-3 natural sentences spoken out loud. Conversational, not salesy. Start with who you help.
- twitter: Max 160 chars. Sharp identity statement. No emojis.
- cold_email: Just the opening line (1-2 sentences). Reference a specific pain point. Make them want to read on.

Be specific, avoid generic phrases like "passionate about" or "results-driven". Sound like a real human, not a robot.`;

    try {
      const response = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }],
        }),
      });

      const data = await response.json();
      const text = data.content?.map(i => i.text || "").join("") || "";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setResults(parsed);
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function copyText(id, text) {
    navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [id]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [id]: false })), 2000);
  }

  const freeFormats = FORMATS.filter(f => f.free);
  const paidFormats = FORMATS.filter(f => !f.free);

  return (
    <>
      <style>{STYLE}</style>
      <div className="app">

        <div className="header">
          <div className="logo">SayWhatYouDo</div>
          <h1 className="headline">
            Stop fumbling.<br />
            <em>Say what you do.</em>
          </h1>
          <p className="subline">
            Describe your work in plain words. Get pitch-perfect bios,
            intros & openers — crafted for every platform.
          </p>
        </div>

        <div className="form-block">
          <div className="form-row">
            <label className="form-label">What do you do?</label>
            <p className="form-hint">Your work, service, or business — in plain language</p>
            <textarea
              rows={2}
              placeholder="e.g. I help early-stage startups with fundraising strategy and pitch deck design"
              value={what}
              onChange={e => setWhat(e.target.value)}
            />
          </div>

          <div className="form-row">
            <label className="form-label">Who do you help?</label>
            <p className="form-hint">Be specific — not "everyone"</p>
            <input
              type="text"
              placeholder="e.g. SaaS founders raising their seed round"
              value={who}
              onChange={e => setWho(e.target.value)}
            />
          </div>

          <div className="form-row" style={{ marginBottom: 0 }}>
            <label className="form-label">What outcome do they get?</label>
            <p className="form-hint">The real result — not the feature, but the feeling</p>
            <input
              type="text"
              placeholder="e.g. They close funding faster and walk into investor meetings with confidence"
              value={outcome}
              onChange={e => setOutcome(e.target.value)}
            />
          </div>
        </div>

        <button className="btn-generate" onClick={generate} disabled={!canGenerate || loading}>
          {loading ? "Generating..." : "Generate My Pitches →"}
        </button>

        {loading && (
          <div className="loader">
            <div className="dots">
              <span /><span /><span />
            </div>
            crafting your voice
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}

        {results && (
          <>
            <div className="results-header">
              <span className="results-title">Your pitches</span>
              <div className="results-line" />
            </div>

            {freeFormats.map(fmt => (
              <div className="format-card" key={fmt.id}>
                <div className="format-card-header">
                  <div className="format-card-label">
                    <span className="format-icon">{fmt.icon}</span>
                    {fmt.label}
                  </div>
                  <button
                    className={`copy-btn ${copied[fmt.id] ? "copied" : ""}`}
                    onClick={() => copyText(fmt.id, results[fmt.id])}
                  >
                    {copied[fmt.id] ? "Copied ✓" : "Copy"}
                  </button>
                </div>
                <div className="format-content">{results[fmt.id]}</div>
              </div>
            ))}

            {/* PAYWALL */}
            {!unlocked ? (
              <div className="paywall-section">
                {paidFormats.map(fmt => (
                  <div className="paywall-card" key={fmt.id}>
                    <div className="paywall-card-header">
                      <div className="paywall-card-label">
                        <span className="format-icon">{fmt.icon}</span>
                        {fmt.label}
                      </div>
                      <span className="locked-badge">Locked</span>
                    </div>
                    <div className="paywall-blur">
                      {PLACEHOLDER_BLUR[fmt.id]}
                    </div>
                  </div>
                ))}

                <div className="paywall-cta">
                  <div className="paywall-cta-title">Unlock all 5 formats</div>
                  <div className="paywall-cta-sub">
                    Get Twitter/X bio + Cold Email opener<br />
                    plus PDF export of all your pitches.
                  </div>
                  <button className="btn-unlock" onClick={() => window.open("https://murbin.gumroad.com/l/saywhatyoudo", "_blank")}>
                    Unlock for $7 →
                  </button>
                  <div className="price-tag">One-time payment · No subscription</div>
                </div>
              </div>
            ) : (
              <div className="paywall-section">
                {paidFormats.map(fmt => (
                  <div className="format-card" key={fmt.id}>
                    <div className="format-card-header">
                      <div className="format-card-label">
                        <span className="format-icon">{fmt.icon}</span>
                        {fmt.label}
                      </div>
                      <button
                        className={`copy-btn ${copied[fmt.id] ? "copied" : ""}`}
                        onClick={() => copyText(fmt.id, results[fmt.id])}
                      >
                        {copied[fmt.id] ? "Copied ✓" : "Copy"}
                      </button>
                    </div>
                    <div className="format-content">{results[fmt.id]}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="footer">
          <span className="footer-brand">SayWhatYouDo</span>
          
        </div>

      </div>
    </>
  );
}
